import { Component, Input } from '@angular/core';
import { JobDetailGeneralInformation } from '../job-detail.models';
import {
	CustomFormAccessor,
	FormConfig,
} from 'src/shared/form-models/form-cva.component';
import { NgControl, FormBuilder, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import {
	Division,
	JobEndMarket,
	JobParty,
	JobQuoteStatus,
	JobTrackID,
	JobType,
	OfferedService,
	Railroad,
	RailroadOvertimeBasis,
	RailroadRegularTimeBasis,
} from 'src/shared/api';
import {
	buildArrayFromEnum,
	displayName,
	idComparer,
} from 'src/shared/utilities/utility-functions';
import {
	displayEndMarket,
	displayJobTrackID,
	displayJobType,
	displayQuoteStatus,
	Roles,
} from 'src/shared/enums';
import { BACKSPACE_KEY } from 'src/shared/constants';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import type { RailroadDetailsState } from 'src/admin-app/railroads/railroad-detail/+state/railroad-detail.reducer';
import { JobDetailsState } from '../+state/job-detail.reducer';
import * as JobDetailActions from '../+state/job-detail.actions';
import * as JobDetailSelectors from '../+state/job-detail.selectors';
import { FormInvalidMatcher } from 'src/admin-app/utilities/error-state-matchers';
import { MatSelectChange } from '@angular/material/select';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/admin-app/shared/auth/auth.service';

const jobGeneralInformationFormConfig: FormConfig<JobDetailGeneralInformation> =
	{
		id: [''],
		number: [''],
		flairDocsJobId: [''],
		customer: ['', [Validators.required]],
		railroad: ['', [Validators.required]],
		division: [''],
		subdivision: [''],
		regularTimeBasis: [''],
		overTimeBasis: [''],
		offeredServices: [''],
		team: [''],
		start: [''],
		end: [''],
		permitNumber: [''],
		quoteStatus: [''],
		isSafetyTrainingComplete: [''],
		descriptionOfSchedule: [''],
		equipmentDescription: [''],
		description: ['', [Validators.required]],
		notes: [''],
		originatingPortalUserEmail: [''],
		quoteNotificationRecipients: [''],
		dwrNotificationRecipients: ['', [CustomValidators.isEmailListInvalid]],
		jobSetupFormRefreshDate: [''],
		status: [''],
		trackSupervisor: [''],
		dateCompleted: [''],
		lastInvoiceCutoffDate: [''],
		assignments: [''],
		unassignedShifts: [''],
		railroadSubCode: [''],
		dateRange: [''],
		isNonBillable: [''],
		trackID: [''],
		orderNumber: [''],
		party: ['', [Validators.required]],
		jobType: ['', [Validators.required]],
		endMarket: ['', [Validators.required]],
		daysRequested: [
			'',
			[Validators.required, Validators.min(0), Validators.max(15)],
		],
	};

const formOptions = { validators: [CustomValidators.endDateNotBeforeStart] };

@Component({
	selector: 'rpfsa-job-general-info',
	templateUrl: './general-info.component.html',
	styles: [
		'.red: { color: red !important; background-color: green !important; }',
	],
})
export class JobDetailGeneralInfoComponent extends CustomFormAccessor<JobDetailGeneralInformation> {
	@Input()
	set railroad(railroad: Railroad) {
		if (railroad && railroad.showTrackID) {
			this.form.get('trackID').setValidators(Validators.required);
		}
		if (railroad && railroad.showOrderNumber) {
			this.form.get('orderNumber').setValidators(Validators.required);
		}
	}

	matcher = new FormInvalidMatcher();
	readonly divisions$ = this.store.pipe(
		select(JobDetailSelectors.getDivisionOptions)
	);
	readonly offeredServices$ = this.store.pipe(
		select(JobDetailSelectors.getOfferedServices)
	);

	readonly requireJobDates$ = this.store.pipe(
		select(JobDetailSelectors.getRequireJobDates)
	);

	readonly isOvertimeBasisMatchingRailroad$ = this.store.pipe(
		select(JobDetailSelectors.isOvertimeDivergedFromRailroad),
		map((value) => !value)
	);

	isFlairdocsJob: boolean;
	isBillingOrAdminRole = this.authService.isUserInOneOfTheseRoles([
		Roles.Billing,
		Roles.Admin,
	]);

	readonly quoteStatuses = buildArrayFromEnum(JobQuoteStatus);
	readonly displayQuoteStatus = displayQuoteStatus;
	readonly displayName = displayName;
	readonly idComparer = idComparer;

	readonly regularTimeBasisTypes = buildArrayFromEnum(
		RailroadRegularTimeBasis
	);
	readonly overtimeBasisTypes = buildArrayFromEnum(RailroadOvertimeBasis);

	readonly jobPartyTypes = buildArrayFromEnum(JobParty);
	readonly typesOfWork = buildArrayFromEnum(JobType);
	readonly endMarket = buildArrayFromEnum(JobEndMarket);
	readonly displayEndMarket = displayEndMarket;
	readonly displayJobType = displayJobType;

	readonly jobTrackIDs = buildArrayFromEnum(JobTrackID);
	readonly displayJobTrackID = displayJobTrackID;
	// RP-3404: Refactor CN-hardcoded Track ID and Order Number fields
	readonly showTrackID$ = this.store.pipe(
		select(JobDetailSelectors.getRailroadForJob),
		filter((rail) => !!rail),
		map((rail) => rail.showTrackID)
	);
	readonly showOrderNumber$ = this.store.pipe(
		select(JobDetailSelectors.getRailroadForJob),
		filter((rail) => !!rail),
		map((rail) => rail.showOrderNumber)
	);

	readonly railroadOvertimeBasis$ = this.store.pipe(
		select(JobDetailSelectors.getRailroadForJob),
		filter((rail) => !!rail),
		map((rail) => rail.overtimeBasis)
	);

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly store: Store<{
			jobs: JobDetailsState;
			railroads: RailroadDetailsState;
		}>,
		private readonly authService: AuthService
	) {
		super(ngControl, fb, jobGeneralInformationFormConfig, formOptions);
	}

	ngOnInit() {
		this.form
			.get('regularTimeBasis')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((basis) => {
				if (basis === RailroadRegularTimeBasis.Monthly) {
					this.form
						.get('overTimeBasis')
						.setValue(RailroadOvertimeBasis.Weekly);
				}
			});

		super.ngOnInit();
	}

	divisionSelected(division: Division): void {
		this.store.dispatch(JobDetailActions.divisionSelected({ division }));
	}

	onKeyUpStartDate(key: string) {
		if (key === BACKSPACE_KEY) {
			this.form.get('start').reset('');
		}
	}

	onKeyUpEndDate(key: string) {
		if (key === BACKSPACE_KEY) {
			this.form.get('end').reset('');
		}
	}

	offeredServiceChange(change: MatSelectChange) {
		this.store.dispatch(
			JobDetailActions.offeredServicesUpdated({
				offeredServices: change.value as OfferedService[],
			})
		);
	}

	// model --> view
	writeValue(value: JobDetailGeneralInformation): void {
		if (value) {
			this.form.patchValue(value, { emitEvent: false });
			this.form.markAsUntouched();
			this.isFlairdocsJob = !!value.flairDocsJobId;
		}
	}

	syncToRailroad() {
		this.railroadOvertimeBasis$.subscribe((railroadOvertimeBasis) => {
			const input = this.form.get('overTimeBasis');
			input.setValue(railroadOvertimeBasis);
		});
	}
}
